import { useState, useEffect, useMemo, useCallback } from "react";
import TableCertificado from "../Components/TableCertificados";
import { formatDateTime, decodeJWT } from "../Components/jwtUtils";
import Api from "../Auth/Api";
import { pdf } from "@react-pdf/renderer";
import GerarCertificado from "../Components/GerarCertificado";
import { Modal, Toast, Collapse, Offcanvas, Badge } from "react-bootstrap";
import DropdownMenu from "../Components/DropdownMenu";
import { useAjustes } from "../Context/AjustesContext";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { customStyles } from '../utils/selectStyles';
import { DEFAULT_LISTA_TIPO, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../utils/paginationDefaults';
import { getCertificadoPdfUrl } from '../utils/certificadoPdfUrl';

/** Data de emissão para UI (igual à do PDF quando a API envia campos extras). Fallback: `cert.data`. */
function getDataEmissaoExibicao(cert) {
    if (!cert) return null;
    return cert.data_emissao_exibicao || cert.status?.data_emissao || cert.data;
}

// `expirado` vem só da data: o certificado arquivado na reciclagem vale até vencer.
// `arquivado` = ciclo anterior; `substituido` = já existe certificado novo.
function isCertificadoProximoVencimento(status) {
    if (!status || status.expirado || status.substituido) return false;
    if (!status.data_expiracao) return false;
    const diasRestantes = Math.ceil(
        (new Date(status.data_expiracao) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return diasRestantes > 0 && diasRestantes <= 30;
}

function Certificados() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivelUsuario = decodeJWT(token).nivel;
    const isAdminOrRH = nivelUsuario === 'Administrador' || nivelUsuario === 'RH';
    const isGestor = nivelUsuario === 'Gestor';
    const isAdminRHOrGestor = isAdminOrRH || isGestor;
    const navigate = useNavigate();

    const { ajustes } = useAjustes();

    const [selectedTipo, setSelectedTipo] = useState(DEFAULT_LISTA_TIPO);
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [showModalDetalhes, setShowModalDetalhes] = useState(false);
    const [certificadoSelecionado, setCertificadoSelecionado] = useState(null);
    const [statusExpiracao, setStatusExpiracao] = useState(null);
    const [certificadosStatus, setCertificadosStatus] = useState({});
    const [showModalInfoExpirado, setShowModalInfoExpirado] = useState(false);
    const [certificadosElegiveisPraEficacia, setCertificadosElegiveisPraEficacia] = useState({});
    const [showModalAvaliacaoEficacia, setShowModalAvaliacaoEficacia] = useState(false);
    const [avaliacaoEficaciaDetalhes, setAvaliacaoEficaciaDetalhes] = useState(null);
    const [showDrawerFiltros, setShowDrawerFiltros] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(true);
    const [showModalRecriar, setShowModalRecriar] = useState(false);
    const [certParaRecriar, setCertParaRecriar] = useState(null);
    const [recriando, setRecriando] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const filterTipo = useMemo(() => [
        { value: 'todos', label: 'Todos' },
        { value: 'favoritos', label: 'Favoritos' },
    ], []);

    const filterStatus = useMemo(() => [
        { value: 'valido', label: 'Válidos' },
        { value: 'expirado', label: 'Expirados' },
        { value: 'proximo', label: 'Próximo de vencimento' },
    ], []);

    //listar todos os cursos
    const [dataTodosCursos, setDataTodosCursos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-todos-cursos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataTodosCursos(response.data);
            } catch (error) {
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    // listar certificados
    const [dataCertificados, setDataCertificados] = useState([]);
    useEffect(() => {
        setLoading(true);
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-certificados/${selectedTipo}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataCertificados(response.data);
            } catch (error) {
                setDataCertificados([]);
                console.error(error.response.data.message)
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [token, selectedTipo, refreshKey])

    // Filtro para seleção por curso
    const [selectCurso, setSelectCurso] = useState(null);
    const [selectUnidade, setSelectUnidade] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    /** Filtro pelos cards KPI — mesma lógica de contagem de `stats` (≠ `selectedStatus` em “Válidos”). */
    const [filtroKpiCert, setFiltroKpiCert] = useState(null); // null | 'validos' | 'expirados' | 'proximos'
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    const applyStatusFilter = useCallback((statusValue) => {
        if (statusValue === 'valido') {
            setFiltroKpiCert(null);
            setSelectedStatus('valido');
        } else if (statusValue === 'expirado') {
            setFiltroKpiCert(null);
            setSelectedStatus('expirado');
        } else if (statusValue === 'proximo') {
            setFiltroKpiCert('proximos');
            setSelectedStatus('proximo');
        } else {
            setFiltroKpiCert(null);
            setSelectedStatus(null);
        }
    }, []);

    const FilterDataProvas = selectCurso ? dataCertificados.filter(item => item.idCurso === selectCurso) : dataCertificados;
    const FilterDataUnidade = selectUnidade ? FilterDataProvas.filter(item => item.unidade === selectUnidade) : FilterDataProvas;

    // Filtro por status (válidos/expirados) ou pelos cards KPI (equivalente aos números dos cards)
    const FilterDataStatus = useMemo(() => {
        if (!filtroKpiCert && !selectedStatus) return FilterDataUnidade;

        return FilterDataUnidade.filter((cert) => {
            const status = certificadosStatus[cert.id];

            if (filtroKpiCert === 'validos') {
                return Boolean(status && !status.expirado);
            }
            if (filtroKpiCert === 'expirados') {
                return Boolean(status && status.expirado);
            }
            if (filtroKpiCert === 'proximos' || selectedStatus === 'proximo') {
                return isCertificadoProximoVencimento(status);
            }

            if (!status) return false;

            if (selectedStatus === 'expirado') {
                return status.expirado;
            }

            if (selectedStatus === 'valido') {
                if (status.expirado) return false;
                if (status.prazo_validade === null || status.prazo_validade === 0) return true;
                if (status.data_expiracao) {
                    return new Date(status.data_expiracao) > new Date();
                }
                return true;
            }

            return true;
        });
    }, [FilterDataUnidade, selectedStatus, certificadosStatus, filtroKpiCert]);

    // Filtro por período de datas
    const FilterDataPeriodo = useMemo(() => {
        let filtered = FilterDataStatus;

        if (dataInicio || dataFim) {
            filtered = filtered.filter(cert => {
                const dataCertificado = new Date(getDataEmissaoExibicao(cert));
                dataCertificado.setHours(0, 0, 0, 0);

                if (dataInicio && dataFim) {
                    const inicio = new Date(dataInicio);
                    inicio.setHours(0, 0, 0, 0);
                    const fim = new Date(dataFim);
                    fim.setHours(23, 59, 59, 999);
                    return dataCertificado >= inicio && dataCertificado <= fim;
                } else if (dataInicio) {
                    const inicio = new Date(dataInicio);
                    inicio.setHours(0, 0, 0, 0);
                    return dataCertificado >= inicio;
                } else if (dataFim) {
                    const fim = new Date(dataFim);
                    fim.setHours(23, 59, 59, 999);
                    return dataCertificado <= fim;
                }

                return true;
            });
        }

        // Aplicar filtro de busca global
        if (globalFilter) {
            filtered = filtered.filter(cert =>
                cert.nomeAluno?.toLowerCase().includes(globalFilter.toLowerCase()) ||
                cert.codigo?.toString().includes(globalFilter) ||
                cert.id?.toString().includes(globalFilter)
            );
        }

        return filtered;
    }, [FilterDataStatus, dataInicio, dataFim, globalFilter]);

    const unidades = useMemo(() => {
        const allUnidades = dataCertificados.map(item => item.unidade);
        return [...new Set(allUnidades)].map(unidade => ({ value: unidade, label: unidade }));
    }, [dataCertificados]);

    // Carregar status de expiração para todos os certificados listados
    useEffect(() => {
        if (dataCertificados.length === 0) return;

        // Criar um objeto onde as chaves são os IDs dos certificados e os valores são seus status
        const statusObj = {};
        const eficaciaObj = {};

        // Obter o ID do usuário logado
        const usuarioLogado = decodeJWT(token);
        const idUsuarioLogado = usuarioLogado.idUsuario;

        dataCertificados.forEach(cert => {
            // Verificar se o certificado tem o objeto status
            if (cert && cert.status) {
                statusObj[cert.id] = cert.status;

                // Verificar se o certificado é elegível para avaliação de eficácia (90 dias após emissão E não expirado)
                const dataEmissao = new Date(getDataEmissaoExibicao(cert));
                const hoje = new Date();
                const diffDias = Math.floor((hoje - dataEmissao) / (1000 * 60 * 60 * 24));

                // Verificar se o usuário logado é o gestor responsável pelo aluno
                const isGestorResponsavel = cert.gestor_responsavel === idUsuarioLogado;

                eficaciaObj[cert.id] = {
                    elegivel:
                        diffDias >= 90 &&
                        !cert.status.expirado &&
                        !cert.status.arquivado &&
                        cert.avaliacao_eficacia !== 1 &&
                        isGestorResponsavel,
                    respondido: cert.avaliacao_eficacia === 1,
                    isGestorResponsavel: isGestorResponsavel
                };
            } else {
                console.warn(`Certificado ID ${cert.id} não possui informações de status`);
                // Criar um objeto status padrão para evitar erros
                statusObj[cert.id] = {
                    expirado: false,
                    arquivado: false,
                    prazo_validade: cert.prazo_validade || 0,
                    data_emissao: getDataEmissaoExibicao(cert),
                    message: 'Informações de status não disponíveis'
                };

                eficaciaObj[cert.id] = {
                    elegivel: false,
                    respondido: cert.avaliacao_eficacia === 1,
                    isGestorResponsavel: false
                };
            }
        });

        setCertificadosStatus(statusObj);
        setCertificadosElegiveisPraEficacia(eficaciaObj);
    }, [dataCertificados, token]);

    // Função para verificar o status de expiração
    const verificarExpiracao = useCallback((certificadoId) => {
        if (certificadosStatus[certificadoId]) {
            setStatusExpiracao(certificadosStatus[certificadoId]);
        } else {
            setStatusExpiracao(null);
        }
    }, [certificadosStatus]);

    // Função para navegar para a avaliação de eficácia
    const navegarParaAvaliacaoEficacia = useCallback((certificadoId) => {
        navigate(`/avaliacao-eficacia/${certificadoId}`);
    }, [navigate]);

    // Função para visualizar os resultados da avaliação de eficácia
    const visualizarAvaliacaoEficacia = useCallback(async (certificadoId) => {
        try {
            const response = await Api.get(`/api/avaliacoes-eficacia-info/${certificadoId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.avaliacao_exists) {
                setAvaliacaoEficaciaDetalhes(response.data);
                setShowModalAvaliacaoEficacia(true);
            } else {
                setToast({
                    header: 'Atenção!',
                    body: 'Não foi possível encontrar os detalhes da avaliação.',
                    type: 'toast-warning'
                });
                setShowToast(true);
            }
        } catch (error) {
            setToast({
                header: 'Erro!',
                body: error.response?.data?.message || 'Erro ao buscar detalhes da avaliação de eficácia.',
                type: 'toast-error'
            });
            setShowToast(true);
        }
    }, [token]);

    // Gerar PDF
    const loadPDFData = useCallback(async (data) => {
        try {
            // Verificar se o certificado está expirado usando o status já carregado
            const statusCertificado = certificadosStatus[data];

            if (!statusCertificado) {
                setToast({
                    header: 'Erro!',
                    body: "Não foi possível verificar o status do certificado. Tente novamente.",
                    type: 'toast-error'
                });
                setShowToast(true);
                return;
            }

            // Se o certificado está expirado e usuário não é Admin/RH, mostra mensagem e não permite download
            if (statusCertificado.expirado && !isAdminOrRH) {
                const mensagem = "Este certificado expirou. É necessário refazer o curso para obter um novo certificado.";

                setToast({
                    header: 'Certificado expirado!',
                    body: mensagem,
                    type: 'toast-warning'
                });
                setShowToast(true);
                return;
            }

            // Se o certificado está expirado mas o usuário é Admin/RH, mostrar aviso mas permitir download
            if (statusCertificado.expirado && isAdminOrRH) {
                const mensagem = "Este certificado está expirado, mas você tem permissão para baixá-lo como " + nivelUsuario + ".";

                setToast({
                    header: 'Aviso!',
                    body: mensagem,
                    type: 'toast-warning'
                });
                setShowToast(true);
            }

            // Buscar os dados para gerar o PDF
            const response = await Api.get(`api/info-certificado/${data}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const certificadoData = response.data[0];

            if (certificadoData?.arquivo_pdf) {
                const arquivoUrl = getCertificadoPdfUrl(certificadoData.arquivo_pdf);
                if (arquivoUrl) {
                    window.open(arquivoUrl, '_blank');
                    return;
                }
            }
            let dataParaCertificado = certificadoData.data;

            // Se for curso misto, usar data da prática se disponível
            if (certificadoData.tipo === 'misto') {
                if (certificadoData.data_pratica) {
                    // Se a data da prática já veio na resposta, usar ela
                    dataParaCertificado = certificadoData.data_pratica;
                } else {
                    // Se não veio, buscar práticas ativas do curso
                    try {
                        const praticasResponse = await Api.get(`api/cursos/${certificadoData.idCurso}/praticas?arquivadas=false`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });

                        // Filtrar pela prática do usuário específico
                        const praticaUsuario = praticasResponse.data.find(
                            p => p.idUsuario === certificadoData.idUsuario
                        );

                        if (praticaUsuario && praticaUsuario.data_treinamento) {
                            dataParaCertificado = praticaUsuario.data_treinamento;
                        }
                    } catch (error) {
                        console.error('Erro ao buscar prática:', error);
                        // Se der erro, continua com a data padrão
                    }
                }
            } else {
                // Se não for curso misto, usar data da avaliação se disponível
                if (certificadoData.data_avaliacao) {
                    dataParaCertificado = certificadoData.data_avaliacao;
                }
            }

            // Gerar o PDF e obter como Blob
            const blob = await pdf(
                <GerarCertificado
                    data={{
                        id: certificadoData.id,
                        nomeAluno: certificadoData.nomeAluno,
                        cpf: certificadoData.cpf,
                        prazo_validade: certificadoData.prazo_validade,
                        nomeCurso: certificadoData.nomeCurso,
                        data: dataParaCertificado,
                        nomeGestor: certificadoData.nomeGestor,
                        certificado: certificadoData.certificado,
                        certificado_frente: certificadoData.certificado_frente,
                        nomeTopico: certificadoData.nomeTopico,
                        cargaHorariaTotal: certificadoData.cargaHorariaTotal,
                        unidade: certificadoData.unidade
                    }}
                    diplomaUrl={ajustes?.diploma || null}
                    empresa={ajustes?.titulo || "Empresa"}
                    textoCertificado={ajustes?.texto_certificado || null}
                />
            ).toBlob();

            // Criar uma URL para o Blob e abrir em uma nova aba
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            setToast({ header: 'Erro!', body: "Erro ao gerar o PDF. Tente novamente.", type: 'toast-error' });
            setShowToast(true);
        }
    }, [token, ajustes.diploma, ajustes.titulo, ajustes.texto_certificado, isAdminOrRH, nivelUsuario, certificadosStatus]);

    const confirmarRecriarCertificado = useCallback(async () => {
        if (!certParaRecriar) return;
        setRecriando(true);
        setShowModalRecriar(false);
        try {
            // 1. Limpa PDF no backend e cicla permissao
            await Api.post(`api/certificado/${certParaRecriar.id}/recriar`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // 2. Busca dados atualizados do certificado
            const infoResp = await Api.get(`api/info-certificado/${certParaRecriar.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const certData = infoResp.data[0];

            let dataParaCertificado = certData.data;
            if (certData.tipo === 'misto') {
                if (certData.data_pratica) {
                    dataParaCertificado = certData.data_pratica;
                } else {
                    try {
                        const praticasResp = await Api.get(`api/cursos/${certData.idCurso}/praticas?arquivadas=false`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        const praticaUsuario = praticasResp.data.find(p => p.idUsuario === certData.idUsuario);
                        if (praticaUsuario?.data_treinamento) dataParaCertificado = praticaUsuario.data_treinamento;
                    } catch (_) { /* mantém data padrão */ }
                }
            } else if (certData.data_avaliacao) {
                dataParaCertificado = certData.data_avaliacao;
            }

            // 3. Gera novo PDF
            const blob = await pdf(
                <GerarCertificado
                    data={{
                        id: certData.id,
                        nomeAluno: certData.nomeAluno,
                        cpf: certData.cpf,
                        prazo_validade: certData.prazo_validade,
                        nomeCurso: certData.nomeCurso,
                        data: dataParaCertificado,
                        nomeGestor: certData.nomeGestor,
                        certificado: certData.certificado,
                        certificado_frente: certData.certificado_frente,
                        nomeTopico: certData.nomeTopico,
                        cargaHorariaTotal: certData.cargaHorariaTotal,
                        unidade: certData.unidade
                    }}
                    diplomaUrl={ajustes?.diploma || null}
                    empresa={ajustes?.titulo || "Empresa"}
                    textoCertificado={ajustes?.texto_certificado || null}
                />
            ).toBlob();

            // 4. Faz upload do novo PDF
            const formData = new FormData();
            formData.append('arquivo', blob, 'certificado.pdf');
            await Api.post(`api/certificado/upload-pdf/${certParaRecriar.id}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setRefreshKey(k => k + 1);
            setToast({ header: 'Sucesso!', body: 'Certificado recriado com sucesso.', type: 'toast-success' });
            setShowToast(true);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao recriar o certificado.', type: 'toast-error' });
            setShowToast(true);
        } finally {
            setRecriando(false);
            setCertParaRecriar(null);
        }
    }, [certParaRecriar, token, ajustes?.diploma, ajustes?.titulo, ajustes?.texto_certificado]);

    // Função para exportar os resultados da avaliação para Excel
    const exportarAvaliacaoEficaciaExcel = useCallback(() => {
        if (!avaliacaoEficaciaDetalhes) return;

        // Dados do aluno e certificado para a planilha
        const certificadoData = {
            "Aluno": avaliacaoEficaciaDetalhes.certificado.nomeAluno,
            "Curso": avaliacaoEficaciaDetalhes.certificado.nomeCurso,
            "Data Emissão Certificado": new Date(avaliacaoEficaciaDetalhes.certificado.data).toLocaleDateString('pt-BR'),
            "Avaliado por": avaliacaoEficaciaDetalhes.avaliacao.nome_gestor,
            "Data Avaliação": new Date(avaliacaoEficaciaDetalhes.avaliacao.data).toLocaleDateString('pt-BR')
        };

        // Dados do gestor para a planilha
        const gestorData = {
            "Nome do Gestor": avaliacaoEficaciaDetalhes.avaliacao.nome_gestor,
            "ID do Gestor": avaliacaoEficaciaDetalhes.avaliacao.id_gestor,
            "Data da Avaliação": new Date(avaliacaoEficaciaDetalhes.avaliacao.data).toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        };

        // Buscar dados adicionais do gestor (opcional)
        Api.get(`/api/usuario/${avaliacaoEficaciaDetalhes.avaliacao.id_gestor}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then(response => {
            if (response.data) {
                // Adicionar mais dados do gestor se disponível
                const gestorCompleto = {
                    ...gestorData,
                    "Email": response.data.email || "Não disponível",
                    "Unidade": response.data.unidade || "Não disponível",
                    "Cargo": response.data.cargo || "Não disponível"
                };

                gerarExcel(certificadoData, gestorCompleto);
            } else {
                gerarExcel(certificadoData, gestorData);
            }
        }).catch(error => {
            // Se não conseguir obter dados adicionais, continua com os dados básicos
            console.warn("Não foi possível obter dados completos do gestor:", error);
            gerarExcel(certificadoData, gestorData);
        });

        // Função para gerar o Excel com os dados disponíveis
        const gerarExcel = (certData, gestData) => {
            // Dados das respostas para a planilha
            const respostasData = Object.entries(avaliacaoEficaciaDetalhes.avaliacao.respostas).map(([idQuestao, resposta]) => ({
                "Pergunta": resposta.questao,
                "Resposta": resposta.resposta
            }));

            // Cria uma nova planilha
            const workbook = XLSX.utils.book_new();

            // Adiciona planilha com dados do certificado
            const worksheetInfo = XLSX.utils.json_to_sheet([certData]);
            XLSX.utils.book_append_sheet(workbook, worksheetInfo, "Informações");

            // Adiciona planilha com dados do gestor
            const worksheetGestor = XLSX.utils.json_to_sheet([gestData]);
            XLSX.utils.book_append_sheet(workbook, worksheetGestor, "Gestor");

            // Adiciona planilha de respostas
            const worksheetRespostas = XLSX.utils.json_to_sheet(respostasData);
            XLSX.utils.book_append_sheet(workbook, worksheetRespostas, "Respostas");

            // Adiciona comentários, se houver
            if (avaliacaoEficaciaDetalhes.avaliacao.comentarios) {
                const comentariosData = [{
                    "Comentários": avaliacaoEficaciaDetalhes.avaliacao.comentarios
                }];
                const worksheetComentarios = XLSX.utils.json_to_sheet(comentariosData);
                XLSX.utils.book_append_sheet(workbook, worksheetComentarios, "Comentários");
            }

            // Gera o arquivo e realiza o download
            const nomeArquivo = `Avaliacao_Eficacia_${avaliacaoEficaciaDetalhes.certificado.nomeAluno.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, nomeArquivo);

            setToast({
                header: 'Sucesso!',
                body: 'Relatório de avaliação de eficácia exportado com sucesso!',
                type: 'toast-success'
            });
            setShowToast(true);
        };
    }, [avaliacaoEficaciaDetalhes, setToast, setShowToast, token]);

    // Componente de card mobile para certificados
    const CertificadoMobileCard = ({ certificado }) => {
        const status = certificadosStatus[certificado.id];

        const getStatusBadge = () => {
            if (!status) return <span className="badge bg-secondary">-</span>;

            if (status.prazo_validade === null || status.prazo_validade === 0) {
                return <span className="badge bg-info">Permanente</span>;
            }

            if (status.expirado) {
                return <span className="badge bg-danger">Expirado</span>;
            }

            return <span className="badge bg-success">Válido</span>;
        };

        return (
            <div className="col-12 mb-2">
                <div className="card border-0 shadow-sm rounded-3">
                    <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                                <h6 className="mb-1 fw-semibold">{certificado.nomeAluno}</h6>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <small className="text-muted">{certificado.codigo}</small>
                                    {getStatusBadge()}
                                </div>
                                <small className="text-muted">{certificado.nomeCurso}</small>
                            </div>
                        </div>

                        {/* Ações */}
                        <div className="d-flex gap-1 pt-2 border-top">
                            {!status?.expirado || isAdminOrRH ? (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => loadPDFData(certificado.id)}
                                    disabled={!isAdminOrRH && status?.expirado}
                                >
                                    <i className="bi bi-box-arrow-in-down"></i>
                                </button>
                            ) : null}
                            {status?.expirado && (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => setShowModalInfoExpirado(true)}
                                    title="Por que está expirado?"
                                >
                                    <i className="bi bi-info-circle"></i>
                                </button>
                            )}
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                    setCertificadoSelecionado(certificado);
                                    verificarExpiracao(certificado.id);
                                    setShowModalDetalhes(true);
                                }}
                            >
                                <i className="bi bi-eye"></i>
                            </button>
                            {isAdminOrRH && (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => { setCertParaRecriar(certificado); setShowModalRecriar(true); }}
                                    title="Recriar Certificado"
                                >
                                    <i className="bi bi-arrow-clockwise"></i>
                                </button>
                            )}
                            {isAdminRHOrGestor && (
                                certificado.avaliacao_eficacia === 1 ? (
                                    <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => visualizarAvaliacaoEficacia(certificado.id)}
                                    >
                                        <i className="bi bi-file-earmark-text"></i>
                                    </button>
                                ) : (
                                    isGestor && (
                                        <button
                                            onClick={() => {
                                                if (certificadosElegiveisPraEficacia[certificado.id]?.elegivel) {
                                                    navegarParaAvaliacaoEficacia(certificado.id);
                                                }
                                            }}
                                            className={`btn btn-sm btn-outline-${certificadosElegiveisPraEficacia[certificado.id]?.elegivel
                                                ? 'warning'
                                                : 'secondary'
                                                }`}
                                            disabled={!certificadosElegiveisPraEficacia[certificado.id]?.elegivel}
                                        >
                                            <i className={`bi bi-${certificadosElegiveisPraEficacia[certificado.id]?.elegivel
                                                ? 'stars'
                                                : 'slash-circle'
                                                }`}></i>
                                        </button>
                                    )
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Componente de visualização mobile
    const MobileCertificadosView = ({ certificados, filterSelect, setSelectCurso, filterTipo, selectedTipo, setSelectedTipo, setSelectUnidade, unidades, dataInicio, setDataInicio, dataFim, setDataFim }) => {
        const [pageIndex, setPageIndex] = useState(0);
        const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
        const [globalFilter, setGlobalFilter] = useState('');
        const [showFilters, setShowFilters] = useState(false);
        const [sortField, setSortField] = useState(null);
        const [sortDirection, setSortDirection] = useState('asc');

        // Filtrar por pesquisa
        const filteredBySearch = certificados.filter(cert =>
            cert.nomeAluno?.toLowerCase().includes(globalFilter.toLowerCase()) ||
            cert.codigo?.toString().includes(globalFilter) ||
            cert.id?.toString().includes(globalFilter)
        );

        // Ordenar certificados
        const sortedCertificados = useMemo(() => {
            if (!sortField) return filteredBySearch;

            const sorted = [...filteredBySearch].sort((a, b) => {
                let aValue, bValue;

                switch (sortField) {
                    case 'nomeAluno':
                        aValue = (a.nomeAluno || '').toLowerCase();
                        bValue = (b.nomeAluno || '').toLowerCase();
                        break;
                    case 'nomeCurso':
                        aValue = (a.nomeCurso || '').toLowerCase();
                        bValue = (b.nomeCurso || '').toLowerCase();
                        break;
                    case 'data':
                        aValue = new Date(getDataEmissaoExibicao(a)).getTime();
                        bValue = new Date(getDataEmissaoExibicao(b)).getTime();
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });

            return sorted;
        }, [filteredBySearch, sortField, sortDirection]);

        const filteredCertificados = sortedCertificados;

        const totalPages = Math.ceil(filteredCertificados.length / pageSize);
        const startIndex = pageIndex * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedCertificados = filteredCertificados.slice(startIndex, endIndex);

        const canPreviousPage = pageIndex > 0;
        const canNextPage = pageIndex < totalPages - 1;

        return (
            <>
                <section className="w-100 mt-3">
                    <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body p-3">
                            <div className="row">
                                <div className="col-12 mb-2">
                                    <label className="form-label text-primary fw-medium small mb-1">Pesquisa</label>
                                    <div className="inner-addon right-addon">
                                        <i className="d-flex bi bi-search" />
                                        <input
                                            value={globalFilter || ""}
                                            onChange={e => {
                                                setGlobalFilter(e.target.value);
                                                setPageIndex(0);
                                            }}
                                            type="search"
                                            placeholder="Pesquise o aluno pelo nome ou matrícula..."
                                            className="form-control py-2"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Collapse in={showFilters}>
                                <div id="filters-collapse-mobile">
                                    <div className="row">
                                        <div className="col-12 mb-2">
                                            <label className="form-label text-primary fw-medium small mb-1">Curso</label>
                                            <Select
                                                options={filterSelect.map((value) => ({
                                                    value: value.id,
                                                    label: value.nome,
                                                }))}
                                                styles={customStyles}
                                                placeholder="Selecione o curso desejado"
                                                onChange={(selectedOption) => {
                                                    setSelectCurso(selectedOption ? selectedOption.value : null);
                                                }}
                                                isClearable
                                            />
                                        </div>
                                        {filterTipo && (
                                            <div className="col-12 mb-2">
                                                <label className="form-label text-primary fw-medium small mb-1">Tipo</label>
                                                <Select
                                                    options={filterTipo.map((value) => ({
                                                        value: value.value,
                                                        label: value.label,
                                                    }))}
                                                    value={filterTipo.find((opt) => opt.value === selectedTipo) || filterTipo[0]}
                                                    styles={customStyles}
                                                    placeholder="Filtro"
                                                    onChange={(selectedOption) => {
                                                        setSelectedTipo(selectedOption ? selectedOption.value : DEFAULT_LISTA_TIPO);
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {unidades && (
                                            <div className="col-12 mb-2">
                                                <label className="form-label text-primary fw-medium small mb-1">Unidade</label>
                                                <Select
                                                    options={unidades}
                                                    styles={customStyles}
                                                    placeholder="Selecione a unidade"
                                                    onChange={(selectedOption) => {
                                                        setSelectUnidade(selectedOption ? selectedOption.value : null);
                                                    }}
                                                    isClearable
                                                />
                                            </div>
                                        )}
                                        <div className="col-12 mb-2">
                                            <label className="form-label text-primary fw-medium small mb-1">Data Início</label>
                                            <input
                                                type="date"
                                                className="form-control py-2"
                                                value={dataInicio}
                                                onChange={(e) => {
                                                    setDataInicio(e.target.value);
                                                    setPageIndex(0);
                                                }}
                                            />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="form-label text-primary fw-medium small mb-1">Data Fim</label>
                                            <input
                                                type="date"
                                                className="form-control py-2"
                                                value={dataFim}
                                                onChange={(e) => {
                                                    setDataFim(e.target.value);
                                                    setPageIndex(0);
                                                }}
                                            />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="form-label text-primary fw-medium small mb-1">Ordenar por</label>
                                            <Select
                                                options={[
                                                    { value: null, label: 'Sem ordenação' },
                                                    { value: 'nomeAluno', label: 'Aluno' },
                                                    { value: 'nomeCurso', label: 'Curso' },
                                                    { value: 'data', label: 'Data' }
                                                ]}
                                                styles={customStyles}
                                                placeholder="Selecione o campo"
                                                value={sortField ? { value: sortField, label: sortField === 'nomeAluno' ? 'Aluno' : sortField === 'nomeCurso' ? 'Curso' : 'Data' } : null}
                                                onChange={(selectedOption) => {
                                                    setSortField(selectedOption ? selectedOption.value : null);
                                                    setPageIndex(0);
                                                }}
                                                isClearable
                                            />
                                        </div>
                                        {sortField && (
                                            <div className="col-12 mb-2">
                                                <label className="form-label text-primary fw-medium small mb-1">Direção</label>
                                                <select
                                                    className="form-select py-2"
                                                    value={sortDirection}
                                                    onChange={e => {
                                                        setSortDirection(e.target.value);
                                                        setPageIndex(0);
                                                    }}
                                                >
                                                    <option value="asc">Crescente (A-Z, Menor-Maior)</option>
                                                    <option value="desc">Decrescente (Z-A, Maior-Menor)</option>
                                                </select>
                                            </div>
                                        )}
                                        <div className="col-12 mb-2">
                                            <label className="form-label text-primary fw-medium small mb-1">Registros por página</label>
                                            <select className="form-select py-2" value={pageSize} onChange={e => {
                                                setPageSize(Number(e.target.value));
                                                setPageIndex(0);
                                            }}>
                                                {[...PAGE_SIZE_OPTIONS].map(size => (
                                                    <option key={size} value={size}>
                                                        {size}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12 mb-2 d-flex align-items-end justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => setShowFilters(!showFilters)}
                                                aria-controls="filters-collapse-mobile"
                                                aria-expanded={showFilters}
                                            >
                                                <i className={`bi ${showFilters ? 'bi-chevron-up' : 'bi-chevron-down'} me-2`}></i>
                                                {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Collapse>
                            {!showFilters && (
                                <div className="row">
                                    <div className="col-12">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => setShowFilters(true)}
                                            aria-controls="filters-collapse-mobile"
                                            aria-expanded={false}
                                        >
                                            <i className="bi bi-chevron-down me-2"></i>
                                            Mostrar Filtros
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <div className="mb-3">
                    <h5 className="mb-3 text-primary">Lista de Certificados</h5>
                    {paginatedCertificados.length > 0 ? (
                        <div className="row">
                            {paginatedCertificados.map((certificado) => (
                                <CertificadoMobileCard key={certificado.id} certificado={certificado} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted mb-0 py-4">Nenhum certificado encontrado</p>
                    )}
                </div>

                {totalPages > 1 && (
                    <div className="card border-0 shadow-sm rounded-4 p-3">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span className="text-muted">
                                Exibindo {Math.min(startIndex + 1, filteredCertificados.length)} - {Math.min(endIndex, filteredCertificados.length)} de {filteredCertificados.length} registros
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted">Página {pageIndex + 1} de {totalPages}</span>
                                <button
                                    className="btn btn-primary btn-sm text-white"
                                    onClick={() => setPageIndex(pageIndex - 1)}
                                    disabled={!canPreviousPage}
                                >
                                    <i className="bi bi-caret-left"></i>
                                </button>
                                <button
                                    className="btn btn-primary btn-sm text-white"
                                    onClick={() => setPageIndex(pageIndex + 1)}
                                    disabled={!canNextPage}
                                >
                                    <i className="bi bi-caret-right"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: () => <span className="text-nowrap">Aluno</span>,
                accessor: 'nomeAluno',
                disableSortBy: false,
                Cell: ({ cell: { value }, row: { original } }) => (
                    <>
                        <div className='fs-6 mb-1'>{value}</div>
                        <div className='d-flex gap-1 flex-wrap'>
                            <span className='badge bg-primary fs-7 py-1 px-2'>Cód: {original.codigo}</span>
                            <span className='badge bg-success fs-7 py-1 px-2'>{original.unidade}</span>
                        </div>
                    </>
                )
            },
            {
                Header: () => <span className="text-nowrap">Curso</span>,
                accessor: 'nomeCurso',
                disableSortBy: false,
                Cell: ({ cell: { value } }) => (
                    <div className="fs-6">{value}</div>
                )
            },
            {
                Header: () => <span className="text-nowrap">Data</span>,
                id: 'data',
                accessor: row => new Date(getDataEmissaoExibicao(row)).getTime(),
                disableSortBy: false,
                Cell: ({ cell: { value }, row: { original } }) => {
                    const date = new Date(getDataEmissaoExibicao(original));
                    const day = date.getDate().toString().padStart(2, '0');
                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                    const year = date.getFullYear();
                    return (
                        <div className="fs-7 text-center text-nowrap">
                            {`${day}/${month}/${year}`}
                        </div>
                    );
                }
            },
            {
                Header: () => <span className="text-nowrap">Status</span>,
                id: 'status',
                accessor: row => row.id,
                Cell: ({ cell: { value }, row: { original } }) => {
                    const status = certificadosStatus[value];
                    if (!status) return <div className="text-center">-</div>;

                    if (status.prazo_validade === null || status.prazo_validade === 0) {
                        return (
                            <div className="text-center">
                                <span className="badge bg-info fs-7">Permanente</span>
                            </div>
                        );
                    }

                    if (status.expirado) {
                        return (
                            <div className="text-center">
                                <span className="badge bg-danger fs-7">Expirado</span>
                            </div>
                        );
                    }

                    return (
                        <div className="text-center">
                            <span className="badge bg-success fs-7">Válido</span>
                            <div className="small text-muted fs-7 mt-1">
                                até {status.data_expiracao ? new Date(status.data_expiracao).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : "N/A"}
                            </div>
                            {status.arquivado && (
                                <div className="small text-muted fs-7" title={status.message}>
                                    {status.substituido ? 'ciclo anterior' : 'em reciclagem'}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: () => <span className="text-nowrap">Ações</span>,
                id: 'acoes',
                accessor: row => row.id,
                disableSortBy: true,
                Cell: ({ cell: { value }, row: { original } }) => {
                    const status = certificadosStatus[value];
                    const isExpirado = status?.expirado;

                    const menuItems = useMemo(() => {
                        const items = [];

                        // Baixar Certificado
                        if (!isExpirado || isAdminOrRH) {
                            items.push({
                                icon: 'box-arrow-in-down',
                                label: 'Baixar Certificado',
                                onClick: () => loadPDFData(original.id),
                                disabled: !isAdminOrRH && isExpirado
                            });
                        }

                        // Por que está expirado?
                        if (isExpirado) {
                            items.push({
                                icon: 'info-circle',
                                label: 'Por que está expirado?',
                                onClick: () => setShowModalInfoExpirado(true)
                            });
                        }

                        // Ver Detalhes
                        items.push({
                            icon: 'eye',
                            label: 'Ver Detalhes',
                            onClick: () => {
                                setCertificadoSelecionado(original);
                                verificarExpiracao(original.id);
                                setShowModalDetalhes(true);
                            }
                        });

                        // Recriar Certificado (apenas Admin/RH)
                        if (isAdminOrRH) {
                            items.push({
                                icon: 'arrow-clockwise',
                                label: 'Recriar Certificado',
                                onClick: () => {
                                    setCertParaRecriar(original);
                                    setShowModalRecriar(true);
                                }
                            });
                        }

                        // Avaliação de Eficácia
                        if (isAdminRHOrGestor) {
                            if (original.avaliacao_eficacia === 1) {
                                items.push({
                                    icon: 'file-earmark-text',
                                    label: 'Ver Avaliação de Eficácia',
                                    onClick: () => visualizarAvaliacaoEficacia(original.id)
                                });
                            } else if (isGestor) {
                                const elegivel = certificadosElegiveisPraEficacia[value]?.elegivel;
                                items.push({
                                    icon: elegivel ? 'stars' : 'slash-circle',
                                    label: 'Responder Avaliação de Eficácia',
                                    onClick: () => {
                                        if (elegivel) {
                                            navegarParaAvaliacaoEficacia(original.id);
                                        }
                                    },
                                    disabled: !elegivel
                                });
                            }
                        }

                        return items;
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [original, value, isExpirado, certificadosElegiveisPraEficacia]);

                    return (
                        <div style={{ position: 'static' }} className="d-flex align-items-center justify-content-center">
                            <DropdownMenu items={menuItems} />
                        </div>
                    );
                }
            },
            {
                Header: 'Codigo',
                accessor: 'codigo',
                visible: false
            }
        ],
        [loadPDFData, verificarExpiracao, certificadosStatus, certificadosElegiveisPraEficacia, isAdminOrRH, isGestor, isAdminRHOrGestor, navegarParaAvaliacaoEficacia, visualizarAvaliacaoEficacia, setCertParaRecriar, setShowModalRecriar]
    )

    // Calcular estatísticas dos certificados
    const stats = useMemo(() => {
        const validos = dataCertificados.filter(cert => {
            const status = certificadosStatus[cert.id];
            return status && !status.expirado;
        }).length;
        
        const expirados = dataCertificados.filter(cert => {
            const status = certificadosStatus[cert.id];
            return status && status.expirado;
        }).length;
        
        const proximosVencimento = dataCertificados.filter(cert => {
            const status = certificadosStatus[cert.id];
            return isCertificadoProximoVencimento(status);
        }).length;
        
        return { validos, expirados, proximosVencimento };
    }, [dataCertificados, certificadosStatus]);

    // Obter nome do curso selecionado para chips
    const cursoSelecionadoNome = useMemo(() => {
        if (!selectCurso) return null;
        const curso = dataTodosCursos.find(c => c.id === selectCurso);
        return curso ? curso.nome : null;
    }, [selectCurso, dataTodosCursos]);

    // Obter label do status selecionado para chips
    const statusSelecionadoLabel = useMemo(() => {
        if (!selectedStatus) return null;
        const status = filterStatus.find(s => s.value === selectedStatus);
        return status ? status.label : null;
    }, [selectedStatus, filterStatus]);

    // Obter label do tipo selecionado para chips
    const tipoSelecionadoLabel = useMemo(() => {
        if (!selectedTipo || selectedTipo === DEFAULT_LISTA_TIPO) return null;
        const tipo = filterTipo.find(t => t.value === selectedTipo);
        return tipo ? tipo.label : null;
    }, [selectedTipo, filterTipo]);

    const kpiCardBaseClass =
        'card border-0 shadow-sm h-100 bg-white position-relative user-select-none';

    const toggleKpiValidos = () => {
        if (filtroKpiCert === 'validos') {
            setFiltroKpiCert(null);
            setSelectedStatus(null);
        } else {
            setSelectedStatus(null);
            setFiltroKpiCert('validos');
        }
    };
    const toggleKpiExpirados = () => {
        if (filtroKpiCert === 'expirados') {
            setFiltroKpiCert(null);
            setSelectedStatus(null);
        } else {
            setSelectedStatus(null);
            setFiltroKpiCert('expirados');
        }
    };
    const toggleKpiProximos = () => {
        if (filtroKpiCert === 'proximos' || selectedStatus === 'proximo') {
            setFiltroKpiCert(null);
            setSelectedStatus(null);
        } else {
            setFiltroKpiCert('proximos');
            setSelectedStatus('proximo');
        }
    };

    const statusFilterValue = useMemo(() => {
        if (selectedStatus === 'proximo' || filtroKpiCert === 'proximos') {
            return filterStatus.find((s) => s.value === 'proximo') || null;
        }
        if (selectedStatus) {
            return filterStatus.find((s) => s.value === selectedStatus) || null;
        }
        return { value: null, label: 'Todos' };
    }, [selectedStatus, filtroKpiCert, filterStatus]);

    const LoadingScreen = () => (
        <div className="d-flex align-items-center justify-content-center p-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
            </div>
        </div>
    );

    return (
        <>
            <div className="fade-in w-100 mt-3" style={{ marginBottom: '100px' }}>
                {/* Header Moderno com Indicadores */}
                <div className="rounded-3 mb-4">
                    <div className="mb-3">
                        <h1 className="text-primary fs-2 fw-bold mb-2">Certificados</h1>
                        <p className="text-muted mb-0">Gerencie, filtre e acompanhe a validade dos certificados emitidos</p>
                    </div>
                </div>

                {!loading && (
                    <div className="rounded-3 mb-4">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <div
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleKpiValidos();
                                    }
                                }}
                                className={`${kpiCardBaseClass} ${filtroKpiCert === 'validos' ? 'border border-2 border-primary' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={toggleKpiValidos}
                            >
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <div className="fs-1 fw-bold" style={{ color: '#0d6efd' }}>{stats.validos}</div>
                                            <div className="text-muted small mt-1">Certificados Válidos</div>
                                        </div>
                                        <i className="bi bi-check-circle" style={{ color: '#0d6efd', fontSize: '1.5rem' }}></i>
                                    </div>
                                    <div className="progress mt-3" style={{ height: '4px' }}>
                                        <div 
                                            className="progress-bar" 
                                            role="progressbar" 
                                            style={{ width: `${dataCertificados.length > 0 ? (stats.validos / dataCertificados.length * 100) : 0}%`, backgroundColor: '#0d6efd' }}
                                            aria-valuenow={stats.validos} 
                                            aria-valuemin="0" 
                                            aria-valuemax={dataCertificados.length}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleKpiExpirados();
                                    }
                                }}
                                className={`${kpiCardBaseClass} ${filtroKpiCert === 'expirados' ? 'border border-2 border-primary' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={toggleKpiExpirados}
                            >
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <div className="fs-1 fw-bold" style={{ color: '#fd7e14' }}>{stats.expirados}</div>
                                            <div className="text-muted small mt-1">Certificados Expirados</div>
                                        </div>
                                        <i className="bi bi-x-circle" style={{ color: '#fd7e14', fontSize: '1.5rem' }}></i>
                                    </div>
                                    <div className="progress mt-3" style={{ height: '4px' }}>
                                        <div 
                                            className="progress-bar" 
                                            role="progressbar" 
                                            style={{ width: `${dataCertificados.length > 0 ? (stats.expirados / dataCertificados.length * 100) : 0}%`, backgroundColor: '#fd7e14' }}
                                            aria-valuenow={stats.expirados} 
                                            aria-valuemin="0" 
                                            aria-valuemax={dataCertificados.length}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleKpiProximos();
                                    }
                                }}
                                className={`${kpiCardBaseClass} ${filtroKpiCert === 'proximos' || selectedStatus === 'proximo' ? 'border border-2 border-primary' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={toggleKpiProximos}
                            >
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <div className="fs-1 fw-bold" style={{ color: '#20c997' }}>{stats.proximosVencimento}</div>
                                            <div className="text-muted small mt-1">Próximos do Vencimento</div>
                                        </div>
                                        <i className="bi bi-exclamation-triangle" style={{ color: '#20c997', fontSize: '1.5rem' }}></i>
                                    </div>
                                    <div className="progress mt-3" style={{ height: '4px' }}>
                                        <div 
                                            className="progress-bar" 
                                            role="progressbar" 
                                            style={{ width: `${dataCertificados.length > 0 ? (stats.proximosVencimento / dataCertificados.length * 100) : 0}%`, backgroundColor: '#20c997' }}
                                            aria-valuenow={stats.proximosVencimento} 
                                            aria-valuemin="0" 
                                            aria-valuemax={dataCertificados.length}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {/* Barra de Filtros Compacta */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label text-primary fw-medium small mb-1">Buscar</label>
                                <div className="inner-addon right-addon">
                                    <i className="d-flex bi bi-search" />
                                    <input
                                        type="search"
                                        value={globalFilter || ""}
                                        onChange={e => setGlobalFilter(e.target.value)}
                                        placeholder="Nome ou matrícula do aluno..."
                                        className="form-control py-2"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-primary fw-medium small mb-1">Curso</label>
                                <Select
                                    options={dataTodosCursos.map((value) => ({
                                        value: value.id,
                                        label: value.nome,
                                    }))}
                                    styles={customStyles}
                                    placeholder="Selecione o curso"
                                    value={selectCurso ? { value: selectCurso, label: dataTodosCursos.find(c => c.id === selectCurso)?.nome } : null}
                                    onChange={(selectedOption) => {
                                        setSelectCurso(selectedOption ? selectedOption.value : null);
                                    }}
                                    isClearable
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label text-primary fw-medium small mb-1">Unidade</label>
                                <Select
                                    options={unidades}
                                    styles={customStyles}
                                    placeholder="Selecione a unidade"
                                    value={selectUnidade ? unidades.find(u => u.value === selectUnidade) : null}
                                    onChange={(selectedOption) => {
                                        setSelectUnidade(selectedOption ? selectedOption.value : null);
                                    }}
                                    isClearable
                                />
                            </div>
                            <div className="col-md-2">
                                <button
                                    className="btn btn-outline-primary w-100 py-2"
                                    onClick={() => setShowDrawerFiltros(true)}
                                >
                                    <i className="bi bi-funnel me-1"></i>
                                    Mais filtros
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chips de Filtros Ativos */}
                {(cursoSelecionadoNome || selectUnidade || statusSelecionadoLabel || filtroKpiCert || dataInicio || dataFim || tipoSelecionadoLabel) && (
                    <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                        {filtroKpiCert === 'validos' && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Válidos
                                <i
                                    className="bi bi-x cursor-pointer"
                                    onClick={() => setFiltroKpiCert(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {filtroKpiCert === 'expirados' && (
                            <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Expirados
                                <i
                                    className="bi bi-x cursor-pointer"
                                    onClick={() => setFiltroKpiCert(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {filtroKpiCert === 'proximos' && selectedStatus !== 'proximo' && (
                            <Badge bg="success" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Próximos do venc. (≤30 dias)
                                <i
                                    className="bi bi-x cursor-pointer"
                                    onClick={() => setFiltroKpiCert(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {cursoSelecionadoNome && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Curso: {cursoSelecionadoNome}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setSelectCurso(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {selectUnidade && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Unidade: {selectUnidade}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setSelectUnidade(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {statusSelecionadoLabel && (
                            <Badge bg="secondary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Status: {statusSelecionadoLabel}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => applyStatusFilter(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {(dataInicio || dataFim) && (
                            <Badge bg="info" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Período: {dataInicio ? new Date(dataInicio).toLocaleDateString('pt-BR') : '...'} até {dataFim ? new Date(dataFim).toLocaleDateString('pt-BR') : '...'}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => {
                                        setDataInicio('');
                                        setDataFim('');
                                    }}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {tipoSelecionadoLabel && (
                            <Badge bg="success" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Tipo: {tipoSelecionadoLabel}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setSelectedTipo(DEFAULT_LISTA_TIPO)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                    </div>
                )}

                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div>
                        {/* Versão Desktop - Tabela */}
                        <div className="d-none d-md-block">
                            <TableCertificado
                                columns={columns}
                                data={FilterDataPeriodo}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                titulo="Lista de Certificados"
                            />
                        </div>

                        {/* Versão Mobile - Cards */}
                        <div className="d-md-none">
                            <MobileCertificadosView
                                certificados={FilterDataPeriodo}
                                filterSelect={dataTodosCursos}
                                setSelectCurso={setSelectCurso}
                                filterTipo={filterTipo}
                                selectedTipo={selectedTipo}
                                setSelectedTipo={setSelectedTipo}
                                setSelectUnidade={setSelectUnidade}
                                unidades={unidades}
                                dataInicio={dataInicio}
                                setDataInicio={setDataInicio}
                                dataFim={dataFim}
                                setDataFim={setDataFim}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Drawer de Filtros Avançados */}
            <Offcanvas show={showDrawerFiltros} onHide={() => setShowDrawerFiltros(false)} placement="end" style={{ width: '400px' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="fw-semibold fs-5 text-primary">Filtros Avançados</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="bg-gray-100 d-flex flex-column">
                    <div className="mb-3">
                        <label className="form-label text-primary fw-medium mb-2">Status</label>
                        <Select
                            options={[{ value: null, label: 'Todos' }, ...filterStatus]}
                            styles={customStyles}
                            placeholder="Filtrar por status"
                            value={statusFilterValue}
                            onChange={(selectedOption) => {
                                applyStatusFilter(selectedOption?.value ?? null);
                            }}
                            isClearable
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-primary fw-medium mb-2">Período</label>
                        <div className="row g-2">
                            <div className="col-12">
                                <label className="form-label small text-muted mb-1">Data Início</label>
                                <input
                                    type="date"
                                    className="form-control py-2"
                                    value={dataInicio || ''}
                                    onChange={(e) => setDataInicio(e.target.value)}
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label small text-muted mb-1">Data Fim</label>
                                <input
                                    type="date"
                                    className="form-control py-2"
                                    value={dataFim || ''}
                                    onChange={(e) => setDataFim(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-primary fw-medium mb-2">Tipo</label>
                        <Select
                            options={filterTipo.map((value) => ({
                                value: value.value,
                                label: value.label,
                            }))}
                            styles={customStyles}
                            placeholder="Selecione o tipo"
                            value={selectedTipo ? filterTipo.find(t => t.value === selectedTipo) : null}
                            onChange={(selectedOption) => {
                                setSelectedTipo(selectedOption ? selectedOption.value : DEFAULT_LISTA_TIPO);
                            }}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-primary fw-medium mb-2">Registros por página</label>
                        <select 
                            className="form-select py-2" 
                            value={pageSize} 
                            onChange={e => setPageSize(Number(e.target.value))}
                        >
                            {[...PAGE_SIZE_OPTIONS].map(size => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mt-auto pt-3 border-top">
                        <div className="d-flex gap-2 w-100">
                            <button
                                className="btn btn-outline-secondary flex-fill"
                                onClick={() => {
                                    applyStatusFilter(null);
                                    setDataInicio('');
                                    setDataFim('');
                                    setSelectedTipo(DEFAULT_LISTA_TIPO);
                                    setPageSize(DEFAULT_PAGE_SIZE);
                                }}
                            >
                                Limpar filtros
                            </button>
                            <button
                                className="btn btn-primary flex-fill"
                                onClick={() => setShowDrawerFiltros(false)}
                            >
                                Aplicar filtros
                            </button>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            <Modal size="lg" show={showModalDetalhes} onHide={() => setShowModalDetalhes(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Detalhes do Certificado</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {certificadoSelecionado && (
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">ID do Certificado</label>
                                                <p className="mb-0">
                                                    <span className="badge bg-primary fs-6">{certificadoSelecionado.id}</span>
                                                </p>
                                            </div>
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">Aluno</label>
                                                <p className="mb-0">{certificadoSelecionado.nomeAluno}</p>
                                            </div>
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">Curso</label>
                                                <p className="mb-0">{certificadoSelecionado.nomeCurso}</p>
                                            </div>
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">Gestor</label>
                                                <p className="mb-0">{certificadoSelecionado.nomeGestor}</p>
                                            </div>
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">Data de Emissão</label>
                                                <p className="mb-0">{formatDateTime(getDataEmissaoExibicao(certificadoSelecionado))}</p>
                                            </div>
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">Unidade</label>
                                                <p className="mb-0">{certificadoSelecionado.unidade}</p>
                                            </div>

                                            {statusExpiracao && (
                                                <div className="mb-3">
                                                    <label className="text-primary fw-medium">Status</label>
                                                    <div className="d-flex align-items-center">
                                                        {statusExpiracao.prazo_validade === null || statusExpiracao.prazo_validade === 0 || Number(statusExpiracao.prazo_validade) === 0 ? (
                                                            <>
                                                                <div className="badge bg-info me-2">Permanente</div>
                                                                <p className="mb-0 small">Este certificado não tem prazo de validade</p>
                                                            </>
                                                        ) : statusExpiracao.expirado ? (
                                                            <>
                                                                <div className="badge me-2 bg-danger">
                                                                    Expirado
                                                                </div>
                                                                {statusExpiracao.message && (
                                                                    <p className="mb-0 small">
                                                                        {statusExpiracao.message}
                                                                    </p>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="badge me-2 bg-success">
                                                                    Válido
                                                                </div>
                                                                <p className="mb-0 small">
                                                                    {statusExpiracao.arquivado
                                                                        ? statusExpiracao.message
                                                                        : `Expira em ${statusExpiracao.data_expiracao ? new Date(statusExpiracao.data_expiracao).toLocaleDateString() : "data não disponível"}`}
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {isAdminRHOrGestor && certificadoSelecionado && (
                                                <div className="mb-3">
                                                    <label className="text-primary fw-medium">Avaliação de Eficácia</label>
                                                    <div className="d-flex align-items-center">
                                                        {certificadoSelecionado.avaliacao_eficacia === 1 ? (
                                                            <>
                                                                <div className="badge bg-success me-2">Respondida</div>
                                                                <p className="mb-0 small">Avaliação de eficácia já foi respondida</p>
                                                            </>
                                                        ) : statusExpiracao?.expirado || statusExpiracao?.arquivado ? (
                                                            <>
                                                                <div className="badge bg-secondary me-2">Não Aplicável</div>
                                                                <p className="mb-0 small">Certificados expirados ou arquivados não podem receber avaliação de eficácia</p>
                                                            </>
                                                        ) : (isGestor || isAdminRHOrGestor) && !certificadosElegiveisPraEficacia[certificadoSelecionado.id]?.isGestorResponsavel ? (
                                                            <>
                                                                <div className="badge bg-secondary me-2">Sem Permissão</div>
                                                                <p className="mb-0 small">Você não é o gestor responsável por este aluno</p>
                                                            </>
                                                        ) : (isGestor || isAdminRHOrGestor) && certificadosElegiveisPraEficacia[certificadoSelecionado.id]?.elegivel ? (
                                                            <>
                                                                <div className="badge bg-warning me-2">Pendente</div>
                                                                <p className="mb-0 small">Este certificado está elegível para avaliação de eficácia</p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="badge bg-secondary me-2">Não Elegível</div>
                                                                <p className="mb-0 small">O certificado precisa ter mais de 90 dias para ser avaliado</p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            className="btn me-2"
                            onClick={() => setShowModalDetalhes(false)}
                        >
                            Fechar
                        </button>
                        <button
                            className="btn btn-primary px-4"
                            onClick={() => {
                                loadPDFData(certificadoSelecionado.id);
                            }}
                            disabled={!isAdminOrRH && statusExpiracao?.expirado}
                        >
                            Baixar Certificado
                        </button>
                        {isAdminRHOrGestor && certificadoSelecionado &&
                            certificadoSelecionado.avaliacao_eficacia === 1 ? (
                            <button
                                className="btn btn-success px-4"
                                onClick={() => {
                                    setShowModalDetalhes(false);
                                    visualizarAvaliacaoEficacia(certificadoSelecionado.id);
                                }}
                            >
                                Ver Avaliação de Eficácia
                            </button>
                        ) : (isGestor && certificadoSelecionado &&
                            certificadosElegiveisPraEficacia[certificadoSelecionado.id]?.elegivel &&
                            certificadoSelecionado.avaliacao_eficacia !== 1 &&
                            !statusExpiracao?.expirado &&
                            !statusExpiracao?.arquivado) && (
                            <button
                                className="btn btn-warning px-4"
                                onClick={() => {
                                    setShowModalDetalhes(false);
                                    navegarParaAvaliacaoEficacia(certificadoSelecionado.id);
                                }}
                            >
                                Responder Avaliação de Eficácia
                            </button>
                        )
                        }
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal size="lg" show={showModalAvaliacaoEficacia} onHide={() => setShowModalAvaliacaoEficacia(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Avaliação de Eficácia</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {avaliacaoEficaciaDetalhes && (
                        <div className="container">
                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <h5 className="card-title text-primary mb-3">Informações Gerais</h5>
                                            <div className="mb-2">
                                                <strong>Aluno:</strong> {avaliacaoEficaciaDetalhes.certificado.nomeAluno}
                                            </div>
                                            <div className="mb-2">
                                                <strong>Curso:</strong> {avaliacaoEficaciaDetalhes.certificado.nomeCurso}
                                            </div>
                                            <div className="mb-2">
                                                <strong>Avaliado por:</strong> {avaliacaoEficaciaDetalhes.avaliacao.nome_gestor}
                                            </div>
                                            <div className="mb-2">
                                                <strong>Data da Avaliação:</strong> {new Date(avaliacaoEficaciaDetalhes.avaliacao.data).toLocaleDateString('pt-BR', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <h5 className="card-title text-primary mb-3">Respostas</h5>
                                            <div className="table-responsive">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Pergunta</th>
                                                            <th className="text-end">Resposta</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(avaliacaoEficaciaDetalhes.avaliacao.respostas).map(([idQuestao, resposta], index) => (
                                                            <tr key={index}>
                                                                <td>{resposta.questao}</td>
                                                                <td className="text-end">
                                                                    <span className={`badge ${resposta.resposta.includes('Excelente') || resposta.resposta.includes('Muito Bom') ? 'bg-success' :
                                                                        resposta.resposta.includes('Satisfatório') ? 'bg-info' :
                                                                            resposta.resposta.includes('Regular') ? 'bg-warning' :
                                                                                resposta.resposta.includes('Insatisfatório') ? 'bg-danger' :
                                                                                    resposta.resposta === 'Sim' ? 'bg-success' :
                                                                                        resposta.resposta === 'Não' ? 'bg-danger' : 'bg-secondary'
                                                                        }`}>
                                                                        {resposta.resposta}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {avaliacaoEficaciaDetalhes.avaliacao.comentarios && (
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-body">
                                                <h5 className="card-title text-primary mb-3">Comentários Adicionais</h5>
                                                <p>{avaliacaoEficaciaDetalhes.avaliacao.comentarios}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button className="btn me-2" onClick={() => setShowModalAvaliacaoEficacia(false)}>
                            Fechar
                        </button>
                        <button
                            className="btn btn-success"
                            onClick={exportarAvaliacaoEficaciaExcel}
                        >
                            <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>
                            Exportar Excel
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            {/* Modal de confirmação: Recriar Certificado */}
            <Modal size='lg' show={showModalRecriar} onHide={() => setShowModalRecriar(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Recriar certificado</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="body">
                                    <div className="d-flex">
                                        <div className="d-flex flex-column w-100">
                                            <div className="row">
                                                <div className="col-12">
                                                    <p>Tem certeza que deseja recriar o certificado de <b>{certParaRecriar?.nomeAluno}</b> no curso <b>{certParaRecriar?.nomeCurso}</b>?</p>
                                                    <p>O arquivo atual será removido e um novo certificado será gerado automaticamente com o modelo e imagem vigentes.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowModalRecriar(false)} disabled={recriando}>Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={confirmarRecriarCertificado} disabled={recriando}>
                            {recriando ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Recriando...</> : 'Confirmar'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal de Informação sobre Certificado Expirado */}
            <Modal
                show={showModalInfoExpirado}
                onHide={() => setShowModalInfoExpirado(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-semibold fs-5">
                        <i className="bi bi-info-circle-fill text-info me-2"></i>
                        Por que este certificado está expirado?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-info">
                        <i className="bi bi-lightbulb-fill me-2"></i>
                        <strong>Informação importante:</strong>
                    </div>
                    <p>
                        Um certificado fica <strong>"expirado"</strong> somente depois que passa a data de validade dele
                        (data de conclusão do treinamento + prazo de validade do curso).
                    </p>
                    <ul>
                        <li className="mb-2">
                            <strong>Reciclagem automática:</strong> 90 dias antes do vencimento, o curso é liberado automaticamente
                            para o aluno refazer. O certificado atual continua válido até a data de vencimento.
                        </li>
                        <li className="mb-2">
                            <strong>Conclusão antecipada:</strong> se o aluno concluir o curso antes do vencimento, os dois certificados
                            ficam válidos ao mesmo tempo até o anterior vencer.
                        </li>
                    </ul>
                    <div className="alert alert-warning mt-3 mb-0">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>O que fazer?</strong> Para obter um novo certificado válido, o aluno precisa concluir o curso liberado para reciclagem.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-primary" onClick={() => setShowModalInfoExpirado(false)}>
                        Entendi
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default Certificados;